import { useEffect, useMemo } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form'
import { Badge } from '@/components/ui/badge'
import { Link2 } from 'lucide-react'
import { useForm, type SubmitHandler } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { toast } from 'sonner'
import { knowledgeApiClient } from '@/lib/api/knowledge.client'
import { useAppAuth } from '@/contexts/app-auth.context'
import type { Pronunciation } from '@super/shared-types'

const pronunciationEditSchema = z.object({
  word: z.string().min(1, 'Word is required').max(255),
  phoneme: z.string().min(1, 'Phoneme is required').max(500),
})

type PronunciationEditFormValues = z.infer<typeof pronunciationEditSchema>

interface PronunciationEditDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  pronunciation: Pronunciation | null
  onSave?: () => void
  mode: 'create' | 'edit'
}

export function PronunciationEditDialog({
  open,
  onOpenChange,
  pronunciation,
  onSave,
  mode,
}: PronunciationEditDialogProps) {
  const { currentCompany, isSuperAdmin } = useAppAuth()

  // Check if pronunciation is attached to any links
  const isAttachedToLink = useMemo(() => {
    return (
      mode === 'edit' && pronunciation?.links && pronunciation.links.length > 0
    )
  }, [mode, pronunciation])

  const form = useForm<PronunciationEditFormValues>({
    resolver: zodResolver(pronunciationEditSchema),
    defaultValues: {
      word: '',
      phoneme: '',
    },
  })

  // Reset form when dialog opens with new pronunciation
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && pronunciation) {
        form.reset({
          word: pronunciation.word,
          phoneme: pronunciation.phoneme,
        })
      } else {
        form.reset({
          word: '',
          phoneme: '',
        })
      }
    }
  }, [open, pronunciation, mode, form])

  const onSubmit: SubmitHandler<PronunciationEditFormValues> = async data => {
    if (!currentCompany?.id) {
      toast.error('Company ID is missing')
      return
    }

    try {
      if (mode === 'create') {
        if (isSuperAdmin) {
          await knowledgeApiClient.createPronunciationForCompany(
            currentCompany.id,
            data,
          )
        } else {
          await knowledgeApiClient.createPronunciation(data)
        }
        toast.success('Pronunciation created successfully')
      } else {
        if (!pronunciation?.id) {
          toast.error('Pronunciation ID is missing')
          return
        }

        if (isSuperAdmin) {
          await knowledgeApiClient.updatePronunciationForCompany(
            pronunciation.id,
            currentCompany.id,
            data,
          )
        } else {
          await knowledgeApiClient.updatePronunciation(pronunciation.id, data)
        }
        toast.success('Pronunciation updated successfully')
      }

      onOpenChange(false)
      onSave?.()
    } catch (error) {
      console.error('Error saving pronunciation:', error)
      toast.error(
        mode === 'create'
          ? 'Failed to create pronunciation'
          : 'Failed to update pronunciation',
      )
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === 'create' ? 'Create pronunciation' : 'Edit pronunciation'}
            {isAttachedToLink && (
              <Badge
                variant="secondary"
                className="text-xs flex items-center gap-1"
              >
                <Link2 className="h-3 w-3" />
                Linked
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="word"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="word">Word</FormLabel>
                  <FormControl>
                    <Input
                      id="word"
                      placeholder="Enter the word"
                      {...field}
                      disabled={isAttachedToLink}
                    />
                  </FormControl>
                  {isAttachedToLink && (
                    <FormDescription className="text-xs ml-3 mb-1">
                      Word cannot be changed for link-attached pronunciations
                    </FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phoneme"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="phoneme">Phoneme</FormLabel>
                  <FormControl>
                    <Input
                      id="phoneme"
                      placeholder="Enter phonetic spelling (e.g., goo gull)"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit">
                {mode === 'create' ? 'Create' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
